<template>
  <div>
    <b-modal
      id="modal-delete-price"
      :no-close-on-backdrop="true"
      :title="$t('common.delete')"
      ok-only
      :ok-title="$t('common.delete')"
      @ok="deleteItem"
    >
      <b-card-text>
        <h5>{{ $t("common.delete-sure") }}</h5>
        {{ $t("common.sure-click") }}
      </b-card-text>
    </b-modal>

    <b-modal
      id="modal-edit-price"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="createMode ? $t('accounting.setPriceSurgery') : $t('common.edit')"
      :ok-title="$t('common.save')"
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <AutoComplete
              v-model="EditForm.surgery_id"
              :label="$t('clinic.Surgery')"
              :placeholder="`${$t('common.select')} ${$t('clinic.Surgery')}`"
              :serializer="(val) => val.id"
              param-query="search"
              endpoint="surgery"
              text-title="title"
            />
          </b-col>
          <b-col md="12">
            <b-form-group :label="$t('common.price')" label-for="PRICE">
              <b-form-input
                v-model="EditForm.price"
                type="number"
                :placeholder="$t('common.price')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3>{{ $t("accounting.setPriceSurgery") }}</h3>
        <b-button
          v-if="$store.state.app.permissions.includes('store-doctorSurgery')"
          variant="primary"
          @click="show(EditForm, 'create')"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          {{ $t("common.add") }} {{ $t("common.price") }}
        </b-button>
      </div>

      <!-- Search Filters -->
      <b-row class="mb-2">
        <b-col md="4" cols="12">
          <AutoComplete
            v-model="surgeryFilter"
            :label="$t('clinic.Surgery')"
            :placeholder="`${$t('common.select')} ${$t('clinic.Surgery')}`"
            param-query="search"
            :serializer="(val) => val.id"
            endpoint="surgery"
            text-title="title"
          />
        </b-col>
        <b-col md="4" cols="12">
          <b-form-group :label="$t('common.price')" label-for="Name">
            <b-form-input
              v-model="priceFilter"
              type="number"
              :placeholder="$t('common.price')"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          cols="12"
          class="d-flex align-items-end justify-content-start mb-1"
        >
          <b-button variant="primary" @click="getDoctorSurgeries()">
            {{ $t("common.search") }}
            <feather-icon icon="SearchIcon" class="ml-50" />
          </b-button>
        </b-col>
      </b-row>

      <!-- Data Table -->
      <b-table
        v-if="!loading"
        :items="data"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :empty-text="$t('common.no_results')"
        class="position-relative"
      >
        <!-- Column: Surgery Name -->
        <template #cell(surgery_name)="data">
          <span class="text-nowrap">
            {{ data.item.surgery_name }}
          </span>
        </template>

        <!-- Column: Price -->
        <template #cell(price)="data">
          <b-badge variant="light-success" class="font-weight-bold">
            {{ data.item.price }} IQD
          </b-badge>
        </template>

        <!-- Column: Created At -->
        <template #cell(created_at)="data">
          <span class="text-nowrap">
            {{ formatDate(data.item.created_at) }}
          </span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-dropdown
              variant="link"
              toggle-class="p-0"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                v-if="
                  $store.state.app.permissions.includes('update-doctorSurgery')
                "
                @click="show(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t("common.edit") }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="
                  $store.state.app.permissions.includes('delete-doctorSurgery')
                "
                @click="deleteModel(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{
                  $t("common.delete")
                }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <template v-if="loading">
        <div>
          <div class="d-flex justify-content-center mb-1">
            <b-spinner :label="$t('common.loading')" />
          </div>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="!loading && data.length > 0" class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
              {{ $t("common.to") }} {{ Math.min(page * take, dataCount) }}
              {{ $t("common.of") }} {{ dataCount }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
            @click="getDoctorSurgeries()"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BModal,
  BCardText,
  BBadge,
} from "bootstrap-vue";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    AutoComplete,
    BModal,
    BCardText,
    BBadge,
  },
  props: {
    doctorId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      take: 10,
      loading: true,
      dataCount: 0,
      data: [],
      EditForm: {
        doctor_id: "",
        surgery_id: "",
        price: "",
        _method: "PUT",
      },
      deleteForm: {},
      createMode: false,
      params: {
        form: {},
        path: "",
      },
      tableColumns: [
        {
          key: "surgery_name",
          label: this.$t("clinic.Surgery"),
          sortable: true,
        },
        {
          key: "price",
          label: this.$t("common.price"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("common.created_at"),
          sortable: true,
        },
        { key: "actions", label: this.$t("common.actions") },
      ],
      page: 1,
      surgeryFilter: null,
      priceFilter: null,
    };
  },
  watch: {
    doctorId() {
      this.getDoctorSurgeries();
    },
  },
  mounted() {
    this.getDoctorSurgeries();
    this.fetchStatistics();
  },
  methods: {
    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          doctor_id: this.doctorId,
          surgery_id: "",
          price: "",
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.EditForm.id = item.id;
        this.EditForm.doctor_id = item.doctor_id;
        this.EditForm.surgery_id = item.surgery_id;
        this.EditForm.price = item.price;
        // eslint-disable-next-line no-underscore-dangle
        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-edit-price");
    },

    async getDoctorSurgeries() {
      this.loading = true;

      let url = `/doctorSurgery?filter[doctor_id]=${this.doctorId}&page=${this.page}&take=${this.take}&sort=-id`;

      if (this.surgeryFilter) {
        url += `&filter[surgery_id]=${this.surgeryFilter}`;
      }
      if (this.priceFilter) {
        url += `&filter[price]=${this.priceFilter}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    async fetchStatistics() {
      try {
        // Fetch only count with take=1 for efficiency (no need to calculate totals)
        const url = `/doctorSurgery?filter[doctor_id]=${this.doctorId}&take=1&sort=-id`;
        await this.getList(url);

        const totalCount = this.all_data.total || 0;

        this.$emit("statistics-updated", {
          total_surgery_prices_count: totalCount,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching statistics:", error);
      }
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete-price");
    },

    deleteItem() {
      this.$store
        .dispatch("delete", `/doctorSurgery/${this.deleteForm.id}`)
        .then(() => {
          this.getDoctorSurgeries();
          this.fetchStatistics(); // Update statistics

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.delete_success"),
              icon: "CheckIcon",
              variant: "success",
              text: this.$t("common.delete_success"),
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("common.error"),
            },
          });
        });
    },

    submitItem() {
      // Remove empty fields
      const formKeys = Object.keys(this.EditForm);
      formKeys.forEach((key) => {
        if (this.EditForm[key] === "" || this.EditForm[key] === null) {
          delete this.EditForm[key];
        }
      });

      if (this.createMode) {
        this.params.form = this.EditForm;
        this.params.path = "/doctorSurgery";
        this.$store
          .dispatch("create", this.params)
          .then(() => {
            this.getDoctorSurgeries();
            this.fetchStatistics(); // Update statistics
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("common.success"),
              },
            });
          })
          .catch((error) => {
            if (error.response && error.response.status === 400) {
              Object.keys(error.response.data).forEach((key) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: error.response.data[key][0],
                  },
                });
              });
            }
          });
      } else {
        this.params.form = this.EditForm;
        this.params.path = `/doctorSurgery/${this.EditForm.id}`;
        this.$store
          .dispatch("update", this.params)
          .then(() => {
            this.getDoctorSurgeries();
            this.fetchStatistics(); // Update statistics
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("common.success"),
              },
            });
          })
          .catch((error) => {
            if (error.response && error.response.status === 400) {
              Object.keys(error.response.data).forEach((key) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: error.response.data[key][0],
                  },
                });
              });
            }
          });
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    },
  },
};
</script>

<style lang="scss" scoped>
.font-weight-bold {
  font-weight: 600;
}
</style>
